import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';

const GET_EARNINGS_DATA = gql`
  query GetEarningsData {
    allOrders {
      id
      amount
      currency
      status
      created_at
      package {
        id
        name
        price
      }
    }
    allSubscriptions {
      id
      status
      created_at
      package {
        id
        name
        price
      }
      order {
        id
        amount
        status
      }
    }
  }
`;

const TotalEarnings = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('all'); // 'all', 'month', 'week', 'today'

  const { data, loading, error, refetch } = useQuery(GET_EARNINGS_DATA, {
    fetchPolicy: 'cache-and-network'
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filterDataByPeriod = (orders) => {
    if (!orders || selectedPeriod === 'all') return orders;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return orders.filter(order => {
      let orderDate;
      if (typeof order.created_at === 'string' && !isNaN(order.created_at)) {
        orderDate = new Date(parseInt(order.created_at));
      } else if (typeof order.created_at === 'number') {
        orderDate = new Date(order.created_at);
      } else {
        orderDate = new Date(order.created_at);
      }

      if (isNaN(orderDate.getTime())) return false;

      switch (selectedPeriod) {
        case 'today':
          return orderDate >= startOfToday;
        case 'week':
          return orderDate >= startOfWeek;
        case 'month':
          return orderDate >= startOfMonth;
        default:
          return true;
      }
    });
  };

  const calculateEarnings = () => {
    if (!data?.allOrders) return { total: 0, completed: 0, pending: 0, failed: 0, count: 0 };

    const filteredOrders = filterDataByPeriod(data.allOrders);

    const earnings = {
      total: 0,
      completed: 0,
      pending: 0,
      failed: 0,
      count: filteredOrders.length
    };

    filteredOrders.forEach(order => {
      const amount = parseFloat(order.amount) || 0;
      earnings.total += amount;

      switch (order.status) {
        case 'completed':
          earnings.completed += amount;
          break;
        case 'pending':
        case 'processing':
          earnings.pending += amount;
          break;
        case 'failed':
        case 'cancelled':
          earnings.failed += amount;
          break;
        default:
          break;
      }
    });

    return earnings;
  };

  const getTopPackages = () => {
    if (!data?.allOrders) return [];

    const filteredOrders = filterDataByPeriod(data.allOrders.filter(order => order.status === 'completed'));
    const packageStats = {};

    filteredOrders.forEach(order => {
      if (order.package) {
        const packageId = order.package.id;
        if (!packageStats[packageId]) {
          packageStats[packageId] = {
            name: order.package.name,
            count: 0,
            revenue: 0
          };
        }
        packageStats[packageId].count += 1;
        packageStats[packageId].revenue += parseFloat(order.amount) || 0;
      }
    });

    return Object.values(packageStats)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const earnings = calculateEarnings();
  const topPackages = getTopPackages();

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-normal">Loading earnings data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="material-icons-round text-red-500 text-2xl">error</span>
          </div>
          <p className="text-red-600 font-normal mb-2">Error loading earnings data</p>
          <p className="text-gray-500 text-sm mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-normal transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h2 className="font-medium">Revenue Overview</h2>
            <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider mt-0.5">Total Earnings & Sales Analysis</p>
          </div>

          {/* Period Filter */}
          <div className="flex bg-gray-50 border border-gray-100 rounded-xl p-1">
            {[
              { id: 'all', label: 'All Time' },
              { id: 'month', label: 'This Month' },
              { id: 'week', label: 'This Week' },
              { id: 'today', label: 'Today' }
            ].map(period => (
              <button
                key={period.id}
                onClick={() => setSelectedPeriod(period.id)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-normal uppercase tracking-wider transition-all ${selectedPeriod === period.id
                  ? 'bg-white text-bulky-blue-600 shadow-sm'
                  : 'text-gray-400 hover:text-bulky-navy-800'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Earnings Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 rounded-[1.5rem] p-5 text-white relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 blur-xl transition-all group-hover:bg-white/10"></div>
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="w-10 h-10 bg-bulky-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-bulky-blue-500/20">
              <span className="material-icons-round text-xl">payments</span>
            </div>
            <span className="text-bulky-navy-300 text-[10px] font-normal uppercase tracking-wider">Total Revenue</span>
          </div>
          <div className="text-2xl font-normal mb-1 relative z-10">{formatCurrency(earnings.total)}</div>
          <div className="text-bulky-navy-400 text-[9px] font-normal uppercase tracking-wider relative z-10">{earnings.count} Orders</div>
        </div>

        {/* Completed Revenue */}
        <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 rounded-[1.5rem] p-5 text-white relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 blur-xl transition-all group-hover:bg-white/10"></div>
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <span className="material-icons-round text-xl">verified</span>
            </div>
            <span className="text-bulky-navy-300 text-[10px] font-normal uppercase tracking-wider">Completed</span>
          </div>
          <div className="text-2xl font-normal mb-1 relative z-10 text-emerald-400">{formatCurrency(earnings.completed)}</div>
          <div className="text-bulky-navy-400 text-[9px] font-normal uppercase tracking-wider relative z-10">
            {earnings.total > 0 ? Math.round((earnings.completed / earnings.total) * 100) : 0}% Success Rate
          </div>
        </div>

        {/* Pending Revenue */}
        <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 rounded-[1.5rem] p-5 text-white relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 blur-xl transition-all group-hover:bg-white/10"></div>
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <span className="material-icons-round text-xl">hourglass_empty</span>
            </div>
            <span className="text-bulky-navy-300 text-[10px] font-normal uppercase tracking-wider">Pending</span>
          </div>
          <div className="text-2xl font-normal mb-1 relative z-10 text-amber-400">{formatCurrency(earnings.pending)}</div>
          <div className="text-bulky-navy-400 text-[9px] font-normal uppercase tracking-wider relative z-10">
            {earnings.total > 0 ? Math.round((earnings.pending / earnings.total) * 100) : 0}% Pending
          </div>
        </div>

        {/* Failed Revenue */}
        <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 rounded-[1.5rem] p-5 text-white relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 blur-xl transition-all group-hover:bg-white/10"></div>
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="w-10 h-10 bg-rose-500 rounded-xl flex items-center justify-center shadow-lg shadow-rose-500/20">
              <span className="material-icons-round text-xl">error_outline</span>
            </div>
            <span className="text-bulky-navy-300 text-[10px] font-normal uppercase tracking-wider">Failed</span>
          </div>
          <div className="text-2xl font-normal mb-1 relative z-10 text-rose-400">{formatCurrency(earnings.failed)}</div>
          <div className="text-bulky-navy-400 text-[9px] font-normal uppercase tracking-wider relative z-10">
            {earnings.total > 0 ? Math.round((earnings.failed / earnings.total) * 100) : 0}% Failed
          </div>
        </div>
      </div>

      {/* Top Packages */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/30">
          <div>
            <h2 className="font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-bulky-blue-500"></span>
              Top Selling Plans
            </h2>
            <p className="text-[9px] text-gray-400 font-normal uppercase tracking-wider mt-0.5">Best performing packages by revenue</p>
          </div>
          <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center">
            <span className="material-icons-round text-bulky-blue-500 text-lg">insights</span>
          </div>
        </div>

        {topPackages.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {topPackages.map((pkg, index) => (
              <div key={index} className="px-6 py-4 hover:bg-bulky-blue-50/20 transition-all duration-300 group">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-normal text-xs shadow-lg transition-transform group-hover:scale-110 ${index === 0 ? 'bg-amber-500 shadow-amber-500/20' :
                      index === 1 ? 'bg-slate-400 shadow-slate-400/20' :
                        index === 2 ? 'bg-orange-500 shadow-orange-500/20' : 'bg-bulky-navy-800 shadow-bulky-navy-800/10'
                      }`}>
                      {index + 1}
                    </div>
                    <div>
                      <h2 className="font-medium">{pkg.name}</h2>
                      <p className="text-[10px] text-gray-400 font-normal uppercase tracking-wider mt-0.5">{pkg.count} Sales</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-normal text-bulky-navy-800 uppercase tracking-tighter">{formatCurrency(pkg.revenue)}</div>
                    <div className="text-[9px] text-gray-400 font-normal uppercase tracking-wider mt-0.5">Revenue</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-20 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="material-icons-round text-gray-400 text-2xl">inventory_2</span>
            </div>
            <h2 className="font-medium">No Sales Data</h2>
            <p className="text-gray-600">No completed orders found for the selected period.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalEarnings;